import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import * as carouselService from '../services/carousel.service.js';
import ImageCarousel from '../components/common/ImageCarousel.jsx';
import RoleExplainerModal from '../components/onboarding/RoleExplainerModal.jsx';
import RoleIcon from '../components/RoleIcon.jsx';
import { getRoleHomePath } from '../config/navigation.js';
import './Home.css';

function Home() {
  const { user, isAuthenticated, loading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessionExpiredDismissed, setSessionExpiredDismissed] = useState(false);
  const showSessionExpiredBanner = searchParams.get('session_expired') === '1' && !sessionExpiredDismissed;
  const [carouselImages, setCarouselImages] = useState([]);
  const [carouselLoading, setCarouselLoading] = useState(true);
  const [_carouselError, setCarouselError] = useState(null);
  const [autoPlayInterval, setAutoPlayInterval] = useState(5000);
  const [explainerRole, setExplainerRole] = useState(null);

  // Load carousel images (skipped once we redirect authenticated users below)
  useEffect(() => {
    if (isAuthenticated) return undefined;
    loadCarouselImages();
    return undefined;
  }, [isAuthenticated]);

  const loadCarouselImages = async () => {
    try {
      setCarouselError(null);
      const [imagesRes, settingsRes] = await Promise.allSettled([
        carouselService.getActiveImages(),
        carouselService.getSettings(),
      ]);

      if (imagesRes.status === 'fulfilled' && imagesRes.value.success) {
        setCarouselImages(imagesRes.value.data || []);
      } else {
        setCarouselError('Impossible de charger les images');
      }

      if (settingsRes.status === 'fulfilled' && settingsRes.value.success) {
        const interval = Number(settingsRes.value.data?.autoplay_interval_ms);
        if (Number.isFinite(interval) && interval > 0) {
          setAutoPlayInterval(interval);
        }
      }
    } catch (error) {
      console.error('Error loading carousel images:', error);
      setCarouselError('Erreur lors du chargement des images');
    } finally {
      setCarouselLoading(false);
    }
  };

  const dismissSessionExpiredBanner = () => {
    setSessionExpiredDismissed(true);
    if (searchParams.get('session_expired')) {
      const next = new URLSearchParams(searchParams);
      next.delete('session_expired');
      setSearchParams(next, { replace: true });
    }
  };

  if (loading) {
    return (
      <div className="home-loading">
        <div>Chargement...</div>
      </div>
    );
  }

  // Logged-in users never see the public welcome page (logo / deep link to `/`).
  if (isAuthenticated) {
    return <Navigate to={getRoleHomePath(user)} replace />;
  }

  return (
    <main className="home">
      {showSessionExpiredBanner && (
        <div className="home-session-banner" role="status">
          <p>Votre session a été fermée pour inactivité. Reconnectez-vous pour continuer.</p>
          <button
            type="button"
            className="home-session-banner-dismiss"
            onClick={dismissSessionExpiredBanner}
            aria-label="Fermer le message"
          >
            ×
          </button>
        </div>
      )}
      {/* Hero Content - At the top, clean design */}
      <div className="hero-header">
        <div className="hero-header-content">
          <h1>Bienvenue sur Grand-Électeur</h1>
          <p className="home-subtitle">Application d'accompagnement à l'inscription électorale au Sénégal</p>
          <div className="home-actions">
            <a href="/register" className="btn-primary btn-primary-on-dark">S'inscrire</a>
            <a href="/login" className="btn-secondary btn-secondary-on-dark">Se connecter</a>
          </div>
        </div>
      </div>

      {/* Image Carousel Hero */}
      {carouselLoading ? (
        <div className="home-hero-carousel">
          <div className="carousel-loading">
            <p>Chargement...</p>
          </div>
        </div>
      ) : carouselImages.length > 0 ? (
        <div className="home-hero-carousel">
          <ImageCarousel images={carouselImages} autoPlayInterval={autoPlayInterval} />
        </div>
      ) : null}
      
      {/* Fallback Hero Section when no carousel images */}
      {!carouselLoading && carouselImages.length === 0 && (
        <div className="home-hero-fallback">
          <div className="hero-fallback-content">
            <h3>Grand-Électeur</h3>
            <p>L&apos;inscription électorale, simple et accompagnée</p>
          </div>
        </div>
      )}
      
      <section className="home-features" aria-labelledby="features-title">
        <h2 id="features-title" className="home-features-title">
          Découvrez chaque rôle
        </h2>
        <p className="home-features-subtitle">
          Touchez un rôle pour voir une courte explication animée et narrée.
        </p>
        <div className="home-features-grid">
          <button
            type="button"
            className="feature-card feature-card--parrain"
            onClick={() => setExplainerRole('parrain_grand')}
            aria-label="Découvrir le rôle de Parrain Grand Électeur (explication animée)"
          >
            <span className="feature-icon" aria-hidden="true"><RoleIcon role="parrain_grand" /></span>
            <h3>Parrain Grand Électeur</h3>
            <p>Votre influence mobilise votre entourage, au Sénégal comme dans la diaspora. Le financement n'est qu'un appui.</p>
            <span className="feature-cue" aria-hidden="true">▶ Découvrir</span>
          </button>
          <button
            type="button"
            className="feature-card feature-card--relais-prive"
            onClick={() => setExplainerRole('relais_prive')}
            aria-label="Découvrir le rôle de Relais Privé (explication animée)"
          >
            <span className="feature-icon" aria-hidden="true"><RoleIcon role="relais_prive" /></span>
            <h3>Relais Privé</h3>
            <p>Agent de terrain bénévole invité par un Parrain, moteur de sa campagne de massification.</p>
            <span className="feature-cue" aria-hidden="true">▶ Découvrir</span>
          </button>
          <button
            type="button"
            className="feature-card feature-card--relais-public"
            onClick={() => setExplainerRole('relais_public')}
            aria-label="Découvrir le rôle de Relais Public (explication animée)"
          >
            <span className="feature-icon" aria-hidden="true"><RoleIcon role="relais_public" /></span>
            <h3>Relais Public</h3>
            <p>Agent de terrain bénévole validé par la plateforme, visible et contactable par les bénéficiaires de sa localité.</p>
            <span className="feature-cue" aria-hidden="true">▶ Découvrir</span>
          </button>
          <button
            type="button"
            className="feature-card feature-card--beneficiaire"
            onClick={() => setExplainerRole('beneficiaire')}
            aria-label="Découvrir le rôle de Bénéficiaire (explication animée)"
          >
            <span className="feature-icon" aria-hidden="true"><RoleIcon role="beneficiaire" /></span>
            <h3>Bénéficiaire</h3>
            <p>Citoyen demandant à être accompagné pas à pas, du diagnostic jusqu'à son inscription sur la liste électorale.</p>
            <span className="feature-cue" aria-hidden="true">▶ Découvrir</span>
          </button>
          {/* Parrain Sympathisant — définition v4.55 (explication animée : à produire,
              même chaîne que 4.47 — scènes + narration WO/FR/EN). Carte non cliquable
              en attendant : div, pas de bouton sans action. */}
          <div className="feature-card feature-card--parrain-sympa">
            <span className="feature-icon" aria-hidden="true"><RoleIcon role="parrain_sympa" /></span>
            <h3>Parrain Sympathisant</h3>
            <p>
              Le soutien solidaire : vous financez directement (Wave, Orange Money) des dossiers
              vérifiés de citoyens accompagnés sur le terrain. L'application enregistre votre geste
              et votre impact — jamais votre argent.
            </p>
          </div>
        </div>
      </section>

      <RoleExplainerModal
        roleKey={explainerRole}
        open={explainerRole !== null}
        onClose={() => setExplainerRole(null)}
        defaultLang="wo"
      />
    </main>
  );
}

export default Home;
